import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { contractApi, documentApi } from '../../services/api';
import { knownApiErrorText, saveErrorText } from '../../utils/apiError';
import type { Contract, DocumentRecord } from '../../types';
import {
  ATTACHMENT_FORMATS_LABEL,
  MAX_ATTACHMENT_SIZE_MB,
  attachmentErrorCode,
  prepareAttachmentsForUpload,
} from '../../utils/attachmentFiles';

export interface AssignmentValues {
  contractName: string;
  assignmentDescription: string;
  otherDescription: string;
  referToAttachments: boolean;
  variationReason?: string;
}

export const emptyAssignmentValues: AssignmentValues = {
  contractName: '',
  assignmentDescription: '',
  otherDescription: '',
  referToAttachments: false,
};

export const CONTRACT_NAME_MAX = 256;
/** An ÄTA keeps its name as the contract description too, within the old field's length. */
const VARIATION_DESCRIPTION_MAX = 128;

/** Quill leaves `<p><br></p>` behind when emptied; treat markup without text as empty. */
export const isRichTextEmpty = (html: string) =>
  !html || html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim() === '';

export interface AssignmentErrors {
  contractName?: boolean;
  assignment?: boolean;
}

/**
 * Step 1 rule: name + (description OR "står i bilagorna" with ≥1 file). The
 * short description is no longer asked for; the name identifies the contract
 * (ClickUp 869fn1p3g).
 */
export function validateAssignment(values: AssignmentValues, attachmentCount: number): AssignmentErrors {
  const errors: AssignmentErrors = {};
  if (!values.contractName.trim()) errors.contractName = true;
  const coveredByAttachments = values.referToAttachments && attachmentCount > 0;
  if (isRichTextEmpty(values.assignmentDescription) && !coveredByAttachments) errors.assignment = true;
  return errors;
}

export const hasAssignmentErrors = (errors: AssignmentErrors) => Object.values(errors).some(Boolean);

type DescriptionField = 'contractName' | 'assignmentDescription' | 'other' | 'referToAttachments' | 'variationReason' | 'variationOther';

/** An ÄTA's description follows its name; a contract's is no longer written (869fn1p3g). */
const variationDescription = (values: AssignmentValues, variation = false) =>
  variation ? values.contractName.trim().slice(0, VARIATION_DESCRIPTION_MAX) : '';

const descriptionContent = (values: AssignmentValues, variation = false): Partial<Record<DescriptionField, string>> => ({
  contractName: values.contractName.trim(),
  // An emptied Quill editor leaves `<p><br></p>`; store and compare it as ''.
  assignmentDescription: isRichTextEmpty(values.assignmentDescription) ? '' : values.assignmentDescription,
  [variation ? 'variationOther' : 'other']: isRichTextEmpty(values.otherDescription) ? '' : values.otherDescription,
  ...(variation ? { variationReason: values.variationReason ?? '' } : {}),
  referToAttachments: values.referToAttachments ? 'true' : 'false',
});

/**
 * Edit mode, contract out for signing: the API locks it, so writes wait for
 * the guard (which asks the company to withdraw the sending). Implicit saves
 * — autosave on blur, "Nästa" — keep the input in the wizard instead.
 */
export type WriteGuard = () => Promise<boolean>;

/**
 * Files the signing flow adds to the contract (signed PDF, audit trail) are
 * not attachments (bilagor) of the agreement itself.
 */
export const isSigningDocument = (doc: DocumentRecord) =>
  doc.type === 'signed_contract' || doc.type === 'signing_audit_trail';

const clock = () =>
  new Date().toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' });

/**
 * State and persistence for the wizard's first step. The contract draft is
 * created lazily — on the first save, "Nästa" or file upload — because
 * attachments need a contract id. After that every change is saved per field,
 * only when it differs from what the server last confirmed.
 */
export function useContractWizardDraft(projectId: string | undefined, options?: { variation?: boolean; createDraft?: (description: string) => Promise<Contract | null> }) {
  const { t } = useTranslation();
  const [contract, setContract] = useState<Contract | null>(null);
  const [values, setValues] = useState<AssignmentValues>(emptyAssignmentValues);
  const savedRef = useRef<{ description: string; fields: Partial<Record<DescriptionField, string>> } | null>(null);
  const optionsRef = useRef(options);
  optionsRef.current = options;
  const creatingRef = useRef<Promise<Contract | null> | null>(null);
  /** The save in progress; a new save waits for it instead of sending the same fields in parallel. */
  const savingRef = useRef<Promise<boolean> | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatusText] = useState('');
  const [saveStatusAt, setSaveStatusAt] = useState(0);
  const setSaveStatus = useCallback((text: string) => {
    setSaveStatusText(text);
    setSaveStatusAt(Date.now());
  }, []);
  const [attachments, setAttachments] = useState<DocumentRecord[]>([]);
  const [uploading, setUploading] = useState(false);
  const [fileMessage, setFileMessage] = useState<{ type: 'error' | 'success'; text: string } | null>(null);
  const [showErrors, setShowErrors] = useState(false);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [libraryDocs, setLibraryDocs] = useState<DocumentRecord[]>([]);

  const writeGuardRef = useRef<WriteGuard | null>(null);
  // An existing contract (edit mode): "Ändringar sparade" instead of "Utkast sparat".
  const editingRef = useRef(false);
  const valuesRef = useRef(values);
  valuesRef.current = values;
  const contractRef = useRef(contract);
  contractRef.current = contract;

  const reset = useCallback(() => {
    setContract(null);
    setValues(emptyAssignmentValues);
    savedRef.current = null;
    creatingRef.current = null;
    setSaveStatus('');
    setAttachments([]);
    setFileMessage(null);
    setShowErrors(false);
    setLibraryOpen(false);
    writeGuardRef.current = null;
    editingRef.current = false;
  }, [setSaveStatus]);

  /** Passes writes through `guard` first (null: write freely). */
  const setWriteGuard = useCallback((guard: WriteGuard | null) => {
    writeGuardRef.current = guard;
  }, []);

  /** True when writes may go ahead (no guard, or the guard allowed it and was cleared). */
  const passGuard = useCallback(async () => {
    const guard = writeGuardRef.current;
    return guard ? guard() : true;
  }, []);

  /**
   * Opens an existing contract, with the server's values as the saved state.
   * `editing: false` when it is filled in as a new contract (the project's first
   * contract right after "Skapa Projekt"): save status then reads "Utkast sparat".
   */
  const load = useCallback(async (
    existing: Contract,
    options: { editing?: boolean } = {},
  ): Promise<{ values: AssignmentValues; attachments: DocumentRecord[] }> => {
    const [descriptions, allDocs] = await Promise.all([
      contractApi.getContractDescriptions(existing.id),
      projectId ? documentApi.listDocuments(projectId, existing.contractNumber) : Promise.resolve([] as DocumentRecord[]),
    ]);
    const docs = allDocs.filter(doc => !isSigningDocument(doc));
    const loaded: AssignmentValues = {
      contractName: descriptions.contractName?.content ?? '',
      assignmentDescription: descriptions.assignmentDescription?.content ?? '',
      otherDescription: optionsRef.current?.variation ? descriptions.variationOther?.content ?? '' : descriptions.other?.content ?? '',
      variationReason: descriptions.variationReason?.content ?? '',
      referToAttachments: descriptions.referToAttachments?.content === 'true',
    };
    savedRef.current = { description: existing.description?.trim() ?? '', fields: descriptionContent(loaded, optionsRef.current?.variation) };
    creatingRef.current = null;
    editingRef.current = options.editing ?? true;
    contractRef.current = existing;
    valuesRef.current = loaded;
    setContract(existing);
    setValues(loaded);
    setAttachments(docs);
    setSaveStatus('');
    setFileMessage(null);
    setShowErrors(false);
    return { values: loaded, attachments: docs };
  }, [projectId, setSaveStatus]);

  /** Keeps the contract record current after an action outside the hook (e.g. withdrawal). */
  const replaceContract = useCallback((next: Contract) => {
    contractRef.current = next;
    setContract(next);
  }, []);

  const setField = useCallback(<K extends keyof AssignmentValues>(field: K, value: AssignmentValues[K]) => {
    valuesRef.current = { ...valuesRef.current, [field]: value };
    setValues(valuesRef.current);
  }, []);

  const isDirty = (() => {
    const saved = savedRef.current;
    if (!saved) {
      return (
        !!values.contractName.trim() ||
        !isRichTextEmpty(values.assignmentDescription) ||
        !isRichTextEmpty(values.otherDescription) ||
        values.referToAttachments
      );
    }
    const current = descriptionContent(values, optionsRef.current?.variation);
    const description = variationDescription(values, optionsRef.current?.variation);
    return (
      (!!description && saved.description !== description) ||
      (Object.keys(current) as DescriptionField[]).some(key => saved.fields[key] !== current[key])
    );
  })();

  const ensureDraft = useCallback(async (): Promise<Contract | null> => {
    if (contractRef.current) return contractRef.current;
    if (!projectId) return null;
    if (!creatingRef.current) {
      const current = valuesRef.current;
      const description = variationDescription(current, optionsRef.current?.variation) || t('contracts.wizard.assignment.draftFallback');
      creatingRef.current = (optionsRef.current?.createDraft ? optionsRef.current.createDraft(description) : contractApi
        .createContract(projectId, { contractType: 'fixed' }))
        .then(created => {
          if (!created) { creatingRef.current = null; return null; }
          savedRef.current = {
            description: created.description?.trim() ?? '',
            fields: descriptionContent(emptyAssignmentValues, optionsRef.current?.variation),
          };
          setContract(created);
          contractRef.current = created;
          return created;
        })
        .catch(error => {
          creatingRef.current = null;
          throw error;
        });
    }
    return creatingRef.current;
  }, [projectId, t]);

  /** True when a field differs from the server's last confirmed value (an empty name is never sent). */
  const hasUnsavedFields = useCallback(() => {
    const saved = savedRef.current;
    if (!saved) return true;
    const current = valuesRef.current;
    const description = variationDescription(current, optionsRef.current?.variation);
    if (description && description !== saved.description) return true;
    const content = descriptionContent(current, optionsRef.current?.variation);
    return (Object.keys(content) as DescriptionField[]).some(
      field => saved.fields[field] !== content[field] && !(field === 'contractName' && !content.contractName),
    );
  }, []);

  /** Saves every field that differs from the server's last confirmed value. */
  const saveNow = useCallback(async (): Promise<boolean> => {
    // Edit mode: nothing changed means nothing to save (and nothing to withdraw).
    if (editingRef.current && contractRef.current && !hasUnsavedFields()) return true;
    if (!(await passGuard())) return false;
    setSaving(true);
    setSaveStatus(t('contracts.wizard.saveStatus.saving'));
    try {
      const draft = await ensureDraft();
      if (!draft || !savedRef.current) { setSaveStatus(''); return false; }
      const current = valuesRef.current;
      const saved = savedRef.current;

      const description = variationDescription(current, optionsRef.current?.variation);
      if (description && description !== saved.description) {
        const updated = await contractApi.updateContract(draft.id, { description });
        saved.description = description;
        setContract(updated);
      }

      const content = descriptionContent(current, optionsRef.current?.variation);
      for (const field of Object.keys(content) as DescriptionField[]) {
        if (saved.fields[field] === content[field]) continue;
        // The API rejects an empty contract name; it is required before "Nästa" anyway.
        if (field === 'contractName' && !content.contractName) continue;
        await contractApi.updateContractDescription(draft.id, { field, content: content[field]! });
        saved.fields[field] = content[field];
      }
      setSaveStatus(t(editingRef.current ? 'contracts.wizard.saveStatus.savedChanges' : 'contracts.wizard.saveStatus.saved', { time: clock() }));
      return true;
    } catch (error) {
      console.error('Failed to save contract draft:', error);
      // A refusal the API explains by its code, such as an ÄTA that is already
      // open on the contract or a locked contract, is not solved by trying
      // again (ClickUp 869fkk2jh, 869ev0jcn).
      setSaveStatus(knownApiErrorText(t, error) ?? saveErrorText(t, error, 'contracts.wizard.saveStatus.error'));
      return false;
    } finally {
      setSaving(false);
    }
  }, [ensureDraft, hasUnsavedFields, passGuard, setSaveStatus, t]);

  /**
   * Saves run one at a time (ClickUp 869fgyx56): blur autosave, a checkbox and
   * "Nästa" can all ask for a save at once. A later save waits for the one in
   * progress and then sends only what still differs, so the same field is never
   * sent twice in parallel.
   */
  const save = useCallback(async (): Promise<boolean> => {
    while (savingRef.current) {
      if (!(await savingRef.current.catch(() => false))) return false;
      if (!hasUnsavedFields()) return true;
    }
    const run = saveNow();
    savingRef.current = run;
    try {
      return await run;
    } finally {
      if (savingRef.current === run) savingRef.current = null;
    }
  }, [hasUnsavedFields, saveNow]);

  /** Autosave on blur, but only once a draft exists (nothing is created from a blur). */
  const autosave = useCallback(() => {
    if (contractRef.current && !writeGuardRef.current) void save();
  }, [save]);

  /** For controls that commit on change (checkbox): save the new value, not the pre-render one. */
  const setFieldAndSave = useCallback(<K extends keyof AssignmentValues>(field: K, value: AssignmentValues[K]) => {
    valuesRef.current = { ...valuesRef.current, [field]: value };
    setValues(previous => ({ ...previous, [field]: value }));
    if (contractRef.current && !writeGuardRef.current) void save();
  }, [save]);

  const reloadAttachments = useCallback(async (draft: Contract) => {
    if (!projectId) return [];
    const docs = (await documentApi.listDocuments(projectId, draft.contractNumber)).filter(doc => !isSigningDocument(doc));
    setAttachments(docs);
    return docs;
  }, [projectId]);

  const uploadFiles = useCallback(async (files: File[]) => {
    if (!projectId || files.length === 0) return;
    if (!(await passGuard())) return;
    setUploading(true);
    setFileMessage(null);
    try {
      const draft = await ensureDraft();
      if (!draft) return;
      const { files: prepared, errors } = await prepareAttachmentsForUpload(files);
      if (prepared.length > 0) {
        await documentApi.uploadFiles(projectId, prepared, draft.contractNumber);
        await reloadAttachments(draft);
      }
      if (errors.length > 0) {
        setFileMessage({
          type: 'error',
          text: t('contracts.wizard.files.rejected', {
            reason: t(`contracts.attachments.errors.${errors[0].reason}`, {
              formats: ATTACHMENT_FORMATS_LABEL,
              max: MAX_ATTACHMENT_SIZE_MB,
            }),
            files: errors.map(error => error.name).join(', '),
          }),
        });
      }
    } catch (error) {
      console.error('Failed to upload contract attachments:', error);
      const code = attachmentErrorCode(error);
      const key =
        code === 'UNSUPPORTED_FILE_TYPE'
          ? 'contracts.attachments.errors.unsupportedType'
          : code === 'FILE_EMPTY'
            ? 'contracts.attachments.errors.empty'
            : code === 'FILE_TOO_LARGE'
              ? 'contracts.attachments.errors.tooLarge'
              : code === 'FILE_CONTENT_MISMATCH'
                ? 'contracts.attachments.errors.contentMismatch'
                : 'contracts.attachments.uploadError';
      setFileMessage({ type: 'error', text: t(key, { formats: ATTACHMENT_FORMATS_LABEL, max: MAX_ATTACHMENT_SIZE_MB }) });
    } finally {
      setUploading(false);
    }
  }, [ensureDraft, passGuard, projectId, reloadAttachments, t]);

  const removeAttachment = useCallback(async (doc: DocumentRecord) => {
    if (!contractRef.current || !(await passGuard())) return;
    const draft = contractRef.current;
    setFileMessage(null);
    try {
      // Unlink only: the file stays in the project's document library.
      await documentApi.unlinkFromContract(doc.id);
      const docs = await reloadAttachments(draft);
      // "Står i bilagorna" cannot hold without files; clear it and save that too.
      if (docs.length === 0 && valuesRef.current.referToAttachments) {
        setFieldAndSave('referToAttachments', false);
      }
    } catch (error) {
      console.error('Failed to remove contract attachment:', error);
      setFileMessage({ type: 'error', text: t('contracts.attachments.removeError') });
    }
  }, [passGuard, reloadAttachments, setFieldAndSave, t]);

  const openLibrary = useCallback(async () => {
    if (!projectId) return;
    try {
      const docs = await documentApi.listDocuments(projectId);
      setLibraryDocs(docs.filter(doc => doc.contractNumber === null || doc.contractNumber === undefined));
      setLibraryOpen(true);
    } catch (error) {
      console.error('Failed to load document library:', error);
      setFileMessage({ type: 'error', text: t('contracts.attachments.linkError') });
    }
  }, [projectId, t]);

  const linkFromLibrary = useCallback(async (doc: DocumentRecord) => {
    if (!(await passGuard())) return;
    try {
      const draft = await ensureDraft();
      if (!draft) return;
      await documentApi.linkToContract(doc.id, draft.contractNumber);
      setLibraryDocs(previous => previous.filter(item => item.id !== doc.id));
      await reloadAttachments(draft);
    } catch (error) {
      console.error('Failed to link library document:', error);
      setFileMessage({ type: 'error', text: t('contracts.attachments.linkError') });
    }
  }, [ensureDraft, passGuard, reloadAttachments, t]);

  const validate = (current: AssignmentValues) => {
    return validateAssignment(current, attachments.length);
  };
  const errors = validate(values);

  /** "Nästa" on step 1: show errors if incomplete, otherwise save and continue. */
  const validateAndSave = useCallback(async () => {
    const current = validateAssignment(valuesRef.current, attachments.length);
    if (hasAssignmentErrors(current)) {
      setShowErrors(true);
      return false;
    }
    setShowErrors(false);
    // Sent contract: keep the input in the wizard; it is saved after withdrawal.
    if (writeGuardRef.current) return true;
    return save();
  }, [attachments.length, save]);

  return {
    contract,
    values,
    setField,
    setFieldAndSave,
    dirty: isDirty,
    saving,
    saveStatus,
    saveStatusAt,
    save,
    autosave,
    attachments,
    uploading,
    uploadFiles,
    removeAttachment,
    fileMessage,
    libraryOpen,
    libraryDocs,
    openLibrary,
    closeLibrary: () => setLibraryOpen(false),
    linkFromLibrary,
    errors,
    showErrors,
    validateAndSave,
    reset,
    load,
    replaceContract,
    setWriteGuard,
  };
}

export type ContractWizardDraft = ReturnType<typeof useContractWizardDraft>;
